import React, { useState, useEffect, useCallback } from 'react';
import { db, functions } from '../../firebase';
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
    Box, Typography, Paper, Grid, Button, Select, MenuItem,
    FormControl, InputLabel, CircularProgress, Alert, Chip, Divider
} from '@mui/material';
import { toast } from 'react-toastify';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SanitizationModal from '../SanitizationModal';

    const ClientProcessingTools = ({ clientId, onUpdate }) => {
    // --- CONSOLIDATED LOADING STATE ---
    const [loadingStates, setLoadingStates] = useState({
        sanitization: true,
        financialSanitization: true,
        processingFinancials: false,
        processingSynthesis: false,
    });

    const [loading, setLoading] = useState(false);
    const [loadingSanitization, setLoadingSanitization] = useState(true);
    const [loadingFinancialSanitization, setLoadingFinancialSanitization] = useState(true);
    const [processingFinancials, setProcessingFinancials] = useState(false);
    const [processingSynthesis, setProcessingSynthesis] = useState(false);
    // --- DATA STATES ---
    // State for CEO Questionnaire Sanitization
    const [questionnairesToSanitize, setQuestionnairesToSanitize] = useState([]);
    
    // State for Financial Document Sanitization
    const [financialDocsToSanitize, setFinancialDocsToSanitize] = useState([]);

    // State for Financial Document Processing
    const [sanitizedDocs, setSanitizedDocs] = useState([]);
    const [selectedDoc, setSelectedDoc] = useState('');
    const [financialsProcessed, setFinancialsProcessed] = useState(false);

    // State for CEO Synthesis
    const [completedQuestionnaires, setCompletedQuestionnaires] = useState([]);
    const [selectedQuestionnaire, setSelectedQuestionnaire] = useState('');
    const [synthesisComplete, setSynthesisComplete] = useState(false);

    const [sanitizationModal, setSanitizationModal] = useState({
    open: false,
    pathOrUrl: '',
    docType: ''
});

    // --- START: NEW STATE FOR ASANA WORKFLOW ---
    const [loadingAsanaSanitization, setLoadingAsanaSanitization] = useState(true);
    const [asanaDocsToSanitize, setAsanaDocsToSanitize] = useState([]);
    const [sanitizedAsanaDocs, setSanitizedAsanaDocs] = useState([]);
    const [selectedAsanaDoc, setSelectedAsanaDoc] = useState('');
    const [processingAsana, setProcessingAsana] = useState(false);
    const [asanaProcessed, setAsanaProcessed] = useState(false);
    // --- END: NEW STATE FOR ASANA WORKFLOW ---


    // --- START: NEW FETCH & HANDLER FUNCTIONS FOR ASANA ---
    const fetchAsanaDocsToSanitize = useCallback(async () => {
        if (!clientId) return;
        setLoadingAsanaSanitization(true);
        try {
            const q = query(
                collection(db, "documents"),
                where("clientId", "==", clientId),
                where("type", "==", "asana_data"),
                where("documentStatus", "==", "Awaiting Sanitization")
            );
            const querySnapshot = await getDocs(q);
            const items = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setAsanaDocsToSanitize(items);
        } catch (error) {
            console.error("Error fetching Asana data awaiting sanitization:", error);
            toast.error("Failed to load Asana data for sanitization.");
        } finally {
            setLoadingAsanaSanitization(false);
        }
    }, [clientId]);

    const fetchSanitizedAsanaDocs = useCallback(async () => {
        if (!clientId) return;
        try {
            const q = query(
                collection(db, "documents"),
                where("clientId", "==", clientId),
                where("type", "==", "asana_data"),
                where("documentStatus", "==", "Sanitized")
            );
            const snapshot = await getDocs(q);

            const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setSanitizedAsanaDocs(docs);
        } catch (error) {
            console.error("Error fetching sanitized Asana docs: ", error);
            toast.error("Error fetching sanitized Asana documents.");
        }
    }, [clientId]);


    const handleMarkAsanaDocAsSanitized = async (docId) => {
        if (!clientId || !docId) return;
        try {
            const docRef = doc(db, 'documents', docId);
            await updateDoc(docRef, {
                documentStatus: 'Sanitized'
            });
            toast.success("Asana data marked as sanitized!");
            // Refresh lists to move the item
            fetchAsanaDocsToSanitize();
            fetchSanitizedAsanaDocs();
            if (onUpdate) onUpdate();
        } catch (error) {
            console.error("Error marking Asana data as sanitized:", error);
            toast.error("Failed to update Asana data status.");
        }
    };

    const handlePrepareAsanaData = async () => {
    if (!clientId) return;
    const toastId = toast.loading("Fetching latest Asana project data...");
    try {
        const prepareAsanaDataForSanitization = httpsCallable(functions, 'prepareAsanaDataForSanitization');
        const result = await prepareAsanaDataForSanitization({ clientId });
        if (result.data.success) {
            toast.update(toastId, { render: "Asana data fetched and is ready for sanitization!", type: "success", isLoading: false, autoClose: 5000 });
            fetchAsanaDocsToSanitize(); // Refresh the list
            if (onUpdate) onUpdate();
        } else {
            throw new Error(result.data.message || 'Unknown error occurred.');
        }
    } catch (error) {
        console.error("Error preparing Asana data:", error);
        toast.update(toastId, { render: `Failed to prepare Asana data: ${error.message}`, type: "error", isLoading: false, autoClose: 5000 });
    }
};

const handleProcessAsanaData = async () => {
    if (!selectedAsanaDoc) {
        toast.warn("Please select an Asana document to process.");
        return;
    }
    setProcessingAsana(true);
    try {
        const processClientAsanaData = httpsCallable(functions, 'processClientAsanaData');
        const result = await processClientAsanaData({
            clientId: clientId,
            documentId: selectedAsanaDoc
        });
        if (result.data.success) {
            toast.success("Asana data processed successfully!");
            setAsanaProcessed(true);
            fetchSanitizedAsanaDocs(); // Refresh list to remove processed item
            if (onUpdate) onUpdate();
        } else {
            throw new Error(result.data.message || 'Unknown processing error.');
        }
    } catch (error) {
        console.error("Error processing Asana data:", error);
        toast.error(`Processing failed: ${error.message}`);
    } finally {
        setProcessingAsana(false);
    }
};
    // --- END: NEW FETCH & HANDLER FUNCTIONS FOR ASANA ---

    // Fetch CEO questionnaires awaiting sanitization (PRESERVED WITH ENHANCEMENTS)
    // Add this console log at the very beginning of fetchQuestionnairesToSanitize function
// Replace ONLY the fetchQuestionnairesToSanitize function in your ClientProcessingTools.js

const fetchQuestionnairesToSanitize = useCallback(async () => {
    console.log('[DEBUG] fetchQuestionnairesToSanitize - START');
    console.log('[DEBUG] clientId:', clientId);
    
    if (!clientId) {
        console.log('[DEBUG] No clientId - returning early');
        setLoadingSanitization(false);
        return;
    }

    setLoadingSanitization(true);
    console.log('[DEBUG] Set loadingSanitization to true');
    
    try {
        console.log('[DEBUG] Creating Firestore query...');
        const q = query(
            collection(db, `clients/${clientId}/ceoQuestionnaireResponses`),
            where("documentStatus", "==", "Awaiting Sanitization")
        );
        console.log('[DEBUG] Query created, executing...');

        const querySnapshot = await getDocs(q);
        console.log('[DEBUG] Query executed, snapshot size:', querySnapshot.size);
        console.log('[DEBUG] Snapshot empty?', querySnapshot.empty);

        const items = querySnapshot.docs.map(doc => {
            const data = doc.data();
            console.log('[DEBUG] Processing doc:', doc.id, data);
            return { 
                id: doc.id, 
                ...data,
                submittedAt: data.submittedAt?.toDate() || new Date()
            };
        });
        console.log('[DEBUG] Processed items:', items);
        console.log('[DEBUG] Sample item fields:', items[0] ? Object.keys(items[0]) : 'No items found');
        setQuestionnairesToSanitize(items);
        console.log('[DEBUG] Set questionnairesToSanitize with', items.length, 'items');
        
    } catch (error) {
        console.error("[DEBUG] Error in fetchQuestionnairesToSanitize:", error);
        toast.error("Failed to load questionnaires awaiting sanitization.");
    } finally {
        console.log('[DEBUG] Setting loadingSanitization to false');
        setLoadingSanitization(false);
        console.log('[DEBUG] fetchQuestionnairesToSanitize - COMPLETE');
    }
}, [clientId]);

   // Replace the handleOpenForSanitization function with this:
const handleOpenForSanitization = (pathOrUrl, docType = 'document') => {
    console.log('🔍 Opening sanitization modal for:', pathOrUrl, 'Type:', docType);
    
    // Safety check
    if (!pathOrUrl || typeof pathOrUrl !== 'string') {
        toast.error(`Cannot open ${docType}. The file path or URL is missing.`);
        return;
    }
    
    // Open modal instead of showing alerts
    setSanitizationModal({
        open: true,
        pathOrUrl: pathOrUrl,
        docType: docType
    });
};

// Add this helper function
const handleCloseSanitizationModal = () => {
    setSanitizationModal({
        open: false,
        pathOrUrl: '',
        docType: ''
    });
};

// Add this JSX at the very end of your return statement (after the last </Paper>):
{/* Sanitization Modal */}
<SanitizationModal
    open={sanitizationModal.open}
    onClose={handleCloseSanitizationModal}
    pathOrUrl={sanitizationModal.pathOrUrl}
    docType={sanitizationModal.docType}
/>

    // Handler to mark a questionnaire as sanitized (PRESERVED)
    const handleMarkAsSanitized = async (responseId) => {
        if (!clientId || !responseId) return;

        try {
            const docRef = doc(db, `clients/${clientId}/ceoQuestionnaireResponses`, responseId);
            await updateDoc(docRef, {
                documentStatus: 'Sanitized'
            });

            toast.success("Questionnaire marked as sanitized!");
            
            // Refresh the lists
            fetchQuestionnairesToSanitize();
            fetchCompletedQuestionnaires();
        } catch (error) {
            console.error("Error marking questionnaire as sanitized:", error);
            toast.error("Failed to update questionnaire status.");
        }
    };

    // --- START: NEW FINANCIAL DOCUMENT FUNCTIONS ---

const fetchFinancialDocsToSanitize = useCallback(async () => {
    if (!clientId) return;

    setLoadingFinancialSanitization(true);
    try {
        const q = query(
            collection(db, "documents"),
            where("clientId", "==", clientId),
            where("type", "==", "financial"),
            where("documentStatus", "==", "Awaiting Sanitization")
        );
        const querySnapshot = await getDocs(q);
        const items = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
        console.log('[DEBUG] Financial docs sample fields:', items[0] ? Object.keys(items[0]) : 'No financial docs found');
        setFinancialDocsToSanitize(items);
    } catch (error) {
        console.error("Error fetching financial docs awaiting sanitization:", error);
        toast.error("Failed to load financial documents for sanitization.");
    } finally {
        setLoadingFinancialSanitization(false);
    }
}, [clientId]);

const handleMarkFinancialDocAsSanitized = async (docId) => {
    if (!clientId || !docId) return;

    try {
        const docRef = doc(db, 'documents', docId);
        await updateDoc(docRef, {
            documentStatus: 'Sanitized'
        });

        toast.success("Financial document marked as sanitized!");
        
        // Refresh both lists to move the item from "Awaiting" to the "Process" dropdown
        fetchFinancialDocsToSanitize();
        fetchSanitizedFinancialDocs();
        if (onUpdate) onUpdate();

    } catch (error) {
        console.error("Error marking financial doc as sanitized:", error);
        toast.error("Failed to update financial document status.");
    }
};

// --- END: NEW FINANCIAL DOCUMENT FUNCTIONS ---

    // Fetch sanitized financial docs (PRESERVED EXACTLY)
    const fetchSanitizedFinancialDocs = useCallback(async () => {
        if (!clientId) return;

        setLoading(true);
        try {
            const q = query(
                collection(db, "documents"), 
                where("clientId", "==", clientId), 
                where("type", "==", "financial"), 
                where("documentStatus", "==", "Sanitized")
            );

            const snapshot = await getDocs(q);
            const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setSanitizedDocs(docs);
        } catch (error) {
            console.error("Error fetching sanitized financial docs: ", error);
            toast.error("Error fetching sanitized financial documents.");
        } finally {
            setLoading(false);
        }
    }, [clientId]);

    // Fetch completed questionnaires for synthesis (PRESERVED EXACTLY)
    const fetchCompletedQuestionnaires = useCallback(async () => {
        if (!clientId) return;

        setLoading(true);
        try {
            const q = query(
                collection(db, `clients/${clientId}/ceoQuestionnaireResponses`),
                where("documentStatus", "==", "Sanitized")
            );

            const querySnapshot = await getDocs(q);
            const items = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                submittedAt: doc.data().submittedAt?.toDate() || new Date()
            }));

            setCompletedQuestionnaires(items);
        } catch (error) {
            console.error("Error fetching completed questionnaires: ", error);
            toast.error("Error fetching completed questionnaires for synthesis.");
        } finally {
            setLoading(false);
        }
    }, [clientId]);

    // useEffect to fetch all data
    useEffect(() => {
    // Existing functions
    fetchQuestionnairesToSanitize();
    fetchFinancialDocsToSanitize();
    fetchCompletedQuestionnaires();
    fetchSanitizedFinancialDocs();

    // New Asana functions
    fetchAsanaDocsToSanitize();
    fetchSanitizedAsanaDocs();

    // Reset success states for all workflows
    setSynthesisComplete(false);
    setFinancialsProcessed(false);
    setAsanaProcessed(false); // Reset for Asana
    setSelectedQuestionnaire('');
    setSelectedDoc('');
    setSelectedAsanaDoc(''); // Reset for Asana

}, [
    clientId,
    fetchQuestionnairesToSanitize,
    fetchFinancialDocsToSanitize,
    fetchCompletedQuestionnaires,
    fetchSanitizedFinancialDocs,
    fetchAsanaDocsToSanitize, // Add to dependency array
    fetchSanitizedAsanaDocs   // Add to dependency array
]);

    // Handler for financial processing (PRESERVED EXACTLY + DEBUG)
const handleProcessFinancials = async () => {
    if (!selectedDoc) {
        toast.warn("Please select a document to process.");
        return;
    }

    setProcessingFinancials(true);
    const processSanitizedFinancialText = httpsCallable(functions, 'processSanitizedFinancialText', { timeout: 300000 }); // 5 minutes


    try {
        const documentToProcess = sanitizedDocs.find(d => d.id === selectedDoc);
        
        // 🔍 DEBUG: Log the entire document object
        console.log('[DEBUG] Full document object:', documentToProcess);
        console.log('[DEBUG] Available field names:', Object.keys(documentToProcess || {}));
        console.log('[DEBUG] extractedTextDownloadUrl field:', documentToProcess?.extractedTextDownloadUrl);
        console.log('[DEBUG] Looking for any field containing "storage" or "url":', 
            Object.keys(documentToProcess || {}).filter(key => 
                key.toLowerCase().includes('storage') || key.toLowerCase().includes('url')
      )
  );  
        console.log('[DEBUG] Document ID being processed:', selectedDoc);
        console.log('[DEBUG] Storage path being processed:', documentToProcess.extractedTextStoragePath);
        console.log('[DEBUG] Client ID:', clientId);

        await processSanitizedFinancialText({
        clientId: clientId,
        storagePath: documentToProcess.extractedTextStoragePath,
        documentId: selectedDoc
  });

        toast.success("Financial document processing started successfully!");
        setFinancialsProcessed(true);
        if (onUpdate) onUpdate();
    } catch (error) {
        console.error("Error processing financial document:", error);
        toast.error(`Processing failed: ${error.message}`);
    } finally {
        setProcessingFinancials(false);
    }
};

    // Handler for synthesis processing (PRESERVED EXACTLY)
    const handleRunSynthesis = async () => {
    if (!selectedQuestionnaire) {
        toast.warn("Please select a questionnaire to synthesize.");
        return;
    }

    setProcessingSynthesis(true);
    const synthesizeQuestionnaireNarrativeAI = httpsCallable(functions, 'synthesizeQuestionnaireNarrativeAI');

    try {
        await synthesizeQuestionnaireNarrativeAI({
            clientId: clientId,
            responseId: selectedQuestionnaire
        });

        toast.success("Questionnaire synthesis completed successfully!");
        setSynthesisComplete(true);

        // ✅ Remove synthesized questionnaire from dropdown
        setCompletedQuestionnaires(prev =>
        prev.filter(q => q.id !== selectedQuestionnaire)
   );


        setSelectedQuestionnaire('');
        if (onUpdate) onUpdate();

    } catch (error) {
        console.error("Error running synthesis: ", error);
        toast.error(`Synthesis failed: ${error.message}`);
    } finally {
        setProcessingSynthesis(false); // ✅ Always stop the loading spinner
    }
};


    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>Client Processing Tools</Typography>
            
            <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
                <Typography variant="h6" gutterBottom>Step 1: Sanitize CEO Questionnaires</Typography>
                <Divider sx={{ my: 2 }} />
                
                {/* Show questionnaires awaiting sanitization (ENHANCED EXISTING SECTION) */}
                {loadingSanitization ? (
                    <CircularProgress />
                ) : questionnairesToSanitize.length > 0 ? (
                    <Box>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                            🔍 Questionnaires Awaiting Sanitization:
                        </Typography>
                        {questionnairesToSanitize.map(item => (
                            <Box key={item.id} sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between', 
                                p: 2, 
                                border: '1px solid #ddd', 
                                borderRadius: 1, 
                                mb: 1,
                                backgroundColor: '#fff3cd'
                            }}>
                                <Typography>
                                    Questionnaire Submitted: {item.submittedAt.toLocaleString()} - Status: {item.documentStatus}
                                </Typography>
                                <Box>
                                    <Button
                                        variant="outlined"
                                        onClick={() => handleOpenForSanitization(item.extractedTextStoragePath, 'questionnaire')}


                                        sx={{ mr: 1, textTransform: 'none' }}
                                    >
                                        Open for Sanitization
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={() => handleMarkAsSanitized(item.id)}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        Mark as Sanitized
                                    </Button>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <Alert severity="info">
                    No CEO Questionnaires are currently awaiting sanitization.
                    </Alert>
                )}
            </Paper>

{/* ======================================================================= */}
{/* === START: NEW UI FOR FINANCIAL DOCUMENT SANITIZATION (STEP 2) === */}
{/* ======================================================================= */}

<Paper elevation={3} sx={{ p: 3, mb: 4 }}>
    <Typography variant="h6" gutterBottom>Step 2: Sanitize Financial Documents</Typography>
    <Divider sx={{ my: 2 }} />
    
    {loadingFinancialSanitization ? (
        <CircularProgress />
    ) : financialDocsToSanitize.length > 0 ? (
        <Box>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                🔍 Financial Documents Awaiting Sanitization:
            </Typography>
            {financialDocsToSanitize.map(item => (
                <Box key={item.id} sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between', 
                    p: 2, 
                    border: '1px solid #ddd', 
                    borderRadius: 1, 
                    mb: 1,
                    backgroundColor: '#fff3cd'
                }}>
                    <Typography>
                        {item.label || item.name} (Uploaded: {item.createdAt?.toDate().toLocaleDateString()}) - Status: {item.documentStatus}
                    </Typography>
                    <Box>
                        <Button
                           variant="outlined"
                           onClick={() => handleOpenForSanitization(item.extractedTextStoragePath, 'financial document')}

                           disabled={!item.extractedTextDownloadUrl}
                           sx={{ mr: 1, textTransform: 'none' }}
     >
                           Open for Sanitization
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => handleMarkFinancialDocAsSanitized(item.id)}
                            sx={{ textTransform: 'none' }}
                        >
                            Mark as Sanitized
                        </Button>
                    </Box>
                </Box>
            ))}
        </Box>
    ) : (
        <Alert severity="info">
        No financial documents are currently awaiting sanitization.
        </Alert>
    )}
</Paper>

{/* ======================================================================= */}
{/* === END: NEW UI FOR FINANCIAL DOCUMENT SANITIZATION (STEP 2) === */}
{/* ======================================================================= */}

{/* ======================================================================= */}
{/* === START: NEW UI FOR ASANA DATA SANITIZATION (STEP 3) === */}
{/* ======================================================================= */}
<Paper elevation={3} sx={{ p: 3, mb: 4 }}>
    <Typography variant="h6" gutterBottom>Step 3: Sanitize Asana Data</Typography>
    <Divider sx={{ my: 2 }} />
    <Box sx={{ mb: 2 }}>
        <Button variant="contained" onClick={handlePrepareAsanaData}>
            Fetch & Prepare Asana Data
        </Button>
    </Box>

    {loadingAsanaSanitization ? (
        <CircularProgress />
    ) : asanaDocsToSanitize.length > 0 ? (
        <Box>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                🔍 Asana Data Awaiting Sanitization:
            </Typography>
            {asanaDocsToSanitize.map(item => (
                <Box key={item.id} sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    p: 2, border: '1px solid #ddd', borderRadius: 1, mb: 1, backgroundColor: '#fff3cd'
                }}>
                    <Typography>
                        {item.label || item.name} (Fetched: {item.createdAt?.toDate().toLocaleDateString()})
                    </Typography>
                    <Box>
                        <Button
                            variant="outlined"
                            onClick={() => handleOpenForSanitization(item.extractedTextStoragePath, 'Asana data')}
                            sx={{ mr: 1, textTransform: 'none' }}
                        >
                            Open for Sanitization
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => handleMarkAsanaDocAsSanitized(item.id)}
                            sx={{ textTransform: 'none' }}
                        >
                            Mark as Sanitized
                        </Button>
                    </Box>
                </Box>
            ))}
        </Box>
    ) : (
        <Alert severity="info">
            Click "Fetch & Prepare Asana Data" to begin the optional Asana processing workflow.
        </Alert>
    )}
</Paper>
{/* ======================================================================= */}
{/* === END: NEW UI FOR ASANA DATA SANITIZATION (STEP 3) === */}
{/* ======================================================================= */}
            {/* Steps 2 & 3 - PRESERVED EXACTLY AS THEY WERE */}
            <Grid container spacing={4}>
                <Grid item xs={12} md={4}>
                    <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
                        <Typography variant="h6" gutterBottom>
    Step 4: Synthesize CEO Questionnaire Narrative
</Typography>
<Divider sx={{ my: 2 }} />

{synthesisComplete ? (
  <Alert icon={<CheckCircleIcon fontSize="inherit" />} severity="success" sx={{ my: 2 }}>
    Synthesis is complete! The narrative has been saved to the client's profile. You can view it from the main document history.
  </Alert>
) : (
  <>
    <FormControl fullWidth sx={{ mb: 2 }}>
      <InputLabel id="questionnaire-select-label">Select Completed Questionnaire</InputLabel>
      <Select
        labelId="questionnaire-select-label"
        value={selectedQuestionnaire}
        label="Select Completed Questionnaire"
        onChange={(e) => setSelectedQuestionnaire(e.target.value)}
        disabled={completedQuestionnaires.length === 0 || processingSynthesis}
      >
        {completedQuestionnaires.map((item) => (
          <MenuItem key={item.id} value={item.id}>
            {/* THIS IS THE CORRECTED LINE: */}
            Submitted: {item.submittedAt.toLocaleString()}
          </MenuItem>
        ))}
      </Select>
    </FormControl>

    <Button
      variant="contained"
      onClick={handleRunSynthesis}
      disabled={!selectedQuestionnaire || processingSynthesis}
    >
      {processingSynthesis ? <CircularProgress size={24} /> : 'Run Synthesis'}
    </Button>
  </>
)}
                    </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
                        <Typography variant="h6" gutterBottom>Step 5: Process Sanitized Financial Document</Typography>
                        <Divider sx={{ my: 2 }} />
                        {financialsProcessed ? (
                            <Alert icon={<CheckCircleIcon fontSize="inherit" />} severity="success">
                                A financial document has been successfully processed. See document history for details.
                            </Alert>
                        ) : (
                            <>
                                <FormControl fullWidth sx={{ mb: 2 }}>
                                    <InputLabel id="doc-select-label">Select Sanitized Document</InputLabel>
                                    <Select
                                        labelId="doc-select-label"
                                        value={selectedDoc}
                                        label="Select Sanitized Document"
                                        onChange={(e) => setSelectedDoc(e.target.value)}
                                        disabled={sanitizedDocs.length === 0 || processingFinancials}
                                    >
                                        {sanitizedDocs.map((doc) => (
                                            <MenuItem key={doc.id} value={doc.id}>
                                                {doc.label} <Chip label={doc.name} size="small" sx={{ ml: 1 }} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <Button
                                    variant="contained"
                                    onClick={handleProcessFinancials}
                                    disabled={!selectedDoc || processingFinancials}
                                >
                                    {processingFinancials ? <CircularProgress size={24} /> : 'Process Document'}
                                </Button>
                            </>
                        )}
                    </Paper>
                </Grid>
                {/* ======================================================================= */}
{/* === START: NEW UI FOR ASANA DATA PROCESSING (STEP 6) === */}
{/* ======================================================================= */}
<Grid item xs={12} md={4}>
    <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
        <Typography variant="h6" gutterBottom>Step 6: Process Asana Data</Typography>
        <Divider sx={{ my: 2 }} />
        {asanaProcessed ? (
            <Alert icon={<CheckCircleIcon fontSize="inherit" />} severity="success">
                An Asana data file has been successfully processed. The Operational Brief has been saved to the client's profile.
            </Alert>
        ) : (
            <>
                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="asana-doc-select-label">Select Sanitized Asana Data</InputLabel>
                    <Select
                        labelId="asana-doc-select-label"
                        value={selectedAsanaDoc}
                        label="Select Sanitized Asana Data"
                        onChange={(e) => setSelectedAsanaDoc(e.target.value)}
                        disabled={sanitizedAsanaDocs.length === 0 || processingAsana}
                    >
                        {sanitizedAsanaDocs.map((doc) => (
                            <MenuItem key={doc.id} value={doc.id}>
                                {doc.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button
                    variant="contained"
                    onClick={handleProcessAsanaData}
                    disabled={!selectedAsanaDoc || processingAsana}
                >
                    {processingAsana ? <CircularProgress size={24} /> : 'Process Asana Data'}
                </Button>
            </>
        )}
    </Paper>
</Grid>
{/* ======================================================================= */}
{/* === END: NEW UI FOR ASANA DATA PROCESSING (STEP 6) === */}
{/* ======================================================================= */}
            </Grid>
            {/* Sanitization Modal */}
            <SanitizationModal
                open={sanitizationModal.open}
                onClose={handleCloseSanitizationModal}
                pathOrUrl={sanitizationModal.pathOrUrl}
                docType={sanitizationModal.docType}
            />
        </Box>
    );
};

export default ClientProcessingTools;